import React from 'react';
import {
  ErrorsWrapper,
  FileDataWrapper,
  FileRowsWrapper,
  MainContentWrapper,
} from './Data.styles';
import { SettingsNav } from '../SettingsNav';
import { RouteComponentProps } from 'react-router-dom';
import {
  deleteAllFiles,
  deleteFile,
  fetchFiles,
  getFileStatus,
  getSignalrNegotiationResult,
  runAutoClassification,
} from 'utils/api';
import {
  IAutoClassificationFileStatus,
  IFileData,
  IFileDataType,
  IFileStatus,
  IFileUnknownError,
} from 'utils/types';
import { FileRow } from 'components/FileRow/FileRow';
import { FileRowEmpty } from 'components/FileRow/FileRowEmpty';
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator';
import { UploadFileTile } from 'components/UploadFileTile/UploadFileTile';
import { ErrorPanel, ServerErrorPanel } from 'components/ErrorPanel/ErrorPanel';
import { CLASSIFY_SPEND_LINKS, PERMISSIONS } from 'utils/constants';
import { getLocalStorageItem, setLocalStorageItem } from 'utils/localStorage';
import {
  getFileDataTypeFromLocation,
  getSelectedFiles,
} from '../settings.utils';
import NotificationBar from 'components/NotificationBar/NotificationBar';
import { ApiContext } from 'context/ApiContext';
import { LeftSideBar } from './LeftSideBar';
import { FilesActions } from './FilesActions';
import { ApSignalrConsumer } from '@alixpartners/ui-utils';

type IExportPeriod = {
  year: number;
  period: number;
};

interface IDataState {
  errorData: {
    [IFileDataType.REVENUE]: {};
    [IFileDataType.THIRD_PARTY_SPEND]: {};
    [IFileDataType.PERSONNEL_SPEND]: {};
    [IFileDataType.APPLICATIONS]: {};
  };
  files: IFileData[];
  fileUploadInProgress: boolean;
  getFilesCallback: any;
  isDeleteAllOpen: boolean;
  isClassificationNotification: boolean;
  isClassificationInProgress: boolean;
  isExportThirdPartyOpen: boolean;
  isExportPersonnelOpen: boolean;
  isSuccessNotification: boolean;
  loading: boolean;
  loadingText: string;
  serverError?: IFileUnknownError;
  uploadCanceled: boolean;
  signalrUrl: string;
  signalrAccessToken: string;
  signalrIsFirstTimeConnection: boolean;
}

const EMPTY_ERROR_DATA = {
  [IFileDataType.REVENUE]: {},
  [IFileDataType.THIRD_PARTY_SPEND]: {},
  [IFileDataType.PERSONNEL_SPEND]: {},
  [IFileDataType.APPLICATIONS]: {},
};

interface IDataViewProps extends RouteComponentProps {
  permissions: string[];
}

export class DataView extends React.Component<IDataViewProps, IDataState> {
  state = {
    errorData: EMPTY_ERROR_DATA,
    files: [],
    fileUploadInProgress: false,
    getFilesCallback: null,
    isClassificationNotification: false,
    isClassificationInProgress: false,
    isSuccessNotification: false,
    loading: true,
    loadingText: '',
    isDeleteAllOpen: false,
    isExportThirdPartyOpen: false,
    isExportPersonnelOpen: false,
    serverError: undefined,
    uploadCanceled: false,
    signalrUrl: '',
    signalrAccessToken: '',
    signalrIsFirstTimeConnection: true,
  };

  openDeleteAllDialog = () => {
    this.setState({ isDeleteAllOpen: true });
  };

  closeDeleteAllDialog = () => {
    this.setState({ isDeleteAllOpen: false });
  };

  openThirdPartyDialog = () => {
    this.setState({ isExportThirdPartyOpen: true });
  };

  closeThirdPartyDialog = () => {
    this.setState({ isExportThirdPartyOpen: false });
  };

  openPersonnelDialog = () => {
    this.setState({ isExportPersonnelOpen: true });
  };

  closePersonnelDialog = () => {
    this.setState({ isExportPersonnelOpen: false });
  };

  closeAllExport = () => {
    this.setState({
      isExportPersonnelOpen: false,
      isExportThirdPartyOpen: false,
    });
  };

  setErrorsToLocalStorage = ({
    fileDataType,
    fileName,
    id,
    errorMessage,
  }: {
    fileDataType: IFileDataType;
    fileName: string;
    id: number;
    errorMessage: any;
  }) => {
    let existingErrors = getLocalStorageItem(fileDataType);
    if (existingErrors) {
      existingErrors[id] = {
        fileName,
        errorMessage,
      };
    } else {
      existingErrors = {
        [id]: {
          fileName,
          errorMessage,
        },
      };
    }
    setLocalStorageItem(fileDataType, existingErrors);
  };

  showErrors = ({
    errorMessage,
    fileName,
    fileType,
    id,
    statusCode,
  }: {
    errorMessage: any;
    fileName: string;
    fileType: IFileDataType;
    id: number;
    statusCode: number;
  }) => {
    const { errorData } = this.state;
    this.setState({ fileUploadInProgress: false });
    if (statusCode === 500) {
      this.setState({ serverError: errorMessage });
    } else if (statusCode === 400) {
      this.setState({
        serverError: {
          ErrorMessage: errorMessage,
          CorrelationId: '',
          ErrorCode: statusCode,
        },
      });
    } else {
      this.setErrorsToLocalStorage({
        fileDataType: fileType,
        fileName,
        id,
        errorMessage,
      });
      this.setState({
        errorData: {
          ...errorData,
          [fileType]: {
            [fileName]: errorMessage.errors || errorMessage,
          },
        },
      });
    }
    this.getFilesData();
  };

  convertLocalStorageErrors = (fileDataType: IFileDataType) => {
    const localStorageErrors = getLocalStorageItem(fileDataType);
    if (!localStorageErrors) {
      return [];
    }
    return Object.keys(localStorageErrors).map((fileId) => {
      const fileData = localStorageErrors[fileId];
      return {
        fileId,
        errors: fileData.errorMessage.errors,
        fileName: fileData.fileName,
      };
    });
  };

  startUploadProgress = () => {
    this.setState({ fileUploadInProgress: true, uploadCanceled: false });
  };

  getFileStatus = (id: string) => {
    return getFileStatus(id);
  };

  onResetUpload = () => {
    this.setState({
      fileUploadInProgress: false,
      uploadCanceled: true,
      serverError: undefined,
      errorData: EMPTY_ERROR_DATA,
    });
  };

  onFileUploaded = (id: string, fileType: IFileDataType) => {
    if (this.state.uploadCanceled) {
      return;
    }

    getFileStatus(id).then(
      (resp) => {
        if (this.state.uploadCanceled) {
          return;
        }

        if (
          resp.fileState === IFileStatus.PUBLISHED ||
          resp.fileState === IFileStatus.UPLOADED ||
          resp.fileState === IFileStatus.UPLOAD_FAILED
        ) {
          this.setState({
            fileUploadInProgress: false,
            serverError: undefined,
            errorData: EMPTY_ERROR_DATA,
          });
          this.getFilesData();
        } else {
          setTimeout(() => this.onFileUploaded(id, fileType), 1000);
        }
      },
      (resp) => {
        if (this.state.uploadCanceled) {
          return;
        }

        const { errorData } = this.state;
        if (resp.statusCode === 500) {
          this.setState({ serverError: resp.message });
        } else {
          this.setState({
            errorData: {
              ...errorData,
              [fileType]: resp.message.errors,
            },
          });
        }
        this.setState({ fileUploadInProgress: false });
        this.getFilesData();
      }
    );
  };

  isAutoClassificationInProgress = (files: IFileData[]) =>
    files.some(
      (file) =>
        file.autoClassificationStatus ===
        IAutoClassificationFileStatus.IN_PROGRESS
    );

  isAllThirdPartyClassified = (files: IFileData[]) =>
    files.every((file) =>
      file.fileDataType === IFileDataType.THIRD_PARTY_SPEND
        ? file.autoClassificationStatus ===
          IAutoClassificationFileStatus.CLASSIFIED
        : true
    );

  getFilesData = () => {
    fetchFiles().then((files: IFileData[]) => {
      const isAllClassified = this.isAllThirdPartyClassified(files);
      const isFilesClassificationStatusInProgress =
        this.isAutoClassificationInProgress(files);
      const isClassifying =
        isFilesClassificationStatusInProgress && !isAllClassified;
      this.setState({
        files,
        isClassificationInProgress: isClassifying,
        loading: false,
        loadingText: '',
        fileUploadInProgress: false,
        isDeleteAllOpen: false,
      });
    });
  };

  getPeriodDataFromStr = (s?: string) => {
    if (!s) {
      return undefined;
    }
    const dateArr = s.split('-');
    return {
      year: Number(dateArr[0] || ''),
      period: Number(dateArr[1] || ''),
    };
  };

  deleteAllFiles = () => {
    this.setState({
      loading: true,
      loadingText: 'Deleting all files',
    });
    setLocalStorageItem(IFileDataType.REVENUE, undefined);
    setLocalStorageItem(IFileDataType.THIRD_PARTY_SPEND, undefined);
    setLocalStorageItem(IFileDataType.PERSONNEL_SPEND, undefined);
    setLocalStorageItem(IFileDataType.APPLICATIONS, undefined);
    deleteAllFiles().then(this.getFilesData);
  };

  deleteFile = (id: number) => {
    this.setState({
      loading: true,
      loadingText: 'Deleting a file',
    });
    deleteFile(id).then(this.getFilesData);
    this.deleteFileFromLocalStorage(id);
  };

  deleteFileFromLocalStorage = (id: number) => {
    const { location } = this.props;
    const fileDataType = getFileDataTypeFromLocation(location);
    const fileDataTypeData = getLocalStorageItem(fileDataType);
    try {
      delete fileDataTypeData[id];
    } catch (e) {}
    setLocalStorageItem(fileDataType, fileDataTypeData);
  };

  getDateString = (period: IExportPeriod) =>
    `${period.year}-${
      period.period > 9 ? period.period : `0${period.period}`
    }-01T00:00:00`;

  startAutoClassification = () => {
    this.setState({
      isClassificationNotification: true,
      isClassificationInProgress: true,
    });
    runAutoClassification().then(() => {
      this.getFilesData();

      setTimeout(() => {
        this.setState({ isClassificationNotification: false });
      }, 5000);
    });
  };

  goToClassification = () => {
    const { history, location } = this.props;
    const fileDataType = getFileDataTypeFromLocation(location);
    history.push(CLASSIFY_SPEND_LINKS[fileDataType]);
  };

  async componentDidMount() {
    this.getFilesData();
    const { url, accessToken } = await getSignalrNegotiationResult();
    this.setState({
      signalrUrl: url,
      signalrAccessToken: accessToken,
    });
  }

  getSignalrAccessToken = async (): Promise<string> => {
    const { signalrAccessToken, signalrIsFirstTimeConnection } = this.state;

    if (signalrIsFirstTimeConnection) {
      this.setState({ signalrIsFirstTimeConnection: false });
      return signalrAccessToken;
    }

    const { accessToken } = await getSignalrNegotiationResult();
    return accessToken;
  };

  render() {
    const { history, location, permissions } = this.props;
    const {
      files,
      fileUploadInProgress,
      isClassificationNotification,
      isClassificationInProgress,
      isSuccessNotification,
      loading,
      serverError,
      signalrUrl,
    } = this.state;
    const isDownloadDisabled = !permissions.includes(
      PERMISSIONS.DOWNLOAD_SPEND_DATA
    );
    const isUploadDisabled = !permissions.includes(
      PERMISSIONS.UPLOAD_SPEND_DATA
    );
    const isDeleteDisabled = !permissions.includes(
      PERMISSIONS.DELETE_SPEND_DATA
    );

    const fileDataType = getFileDataTypeFromLocation(location);
    const localStorageErrors = this.convertLocalStorageErrors(fileDataType);

    const selectedFiles = getSelectedFiles(files, fileDataType);

    const fileIds = selectedFiles.map((f: IFileData) => f.id);
    return (
      <React.Fragment>
        <SettingsNav />
        <MainContentWrapper>
          {loading ? (
            <LoadingIndicator loading />
          ) : (
            <>
              {signalrUrl ? (
                <ApSignalrConsumer
                  message="NotificationCreated"
                  onMessage={() => {
                    this.getFilesData();
                    this.setState({ isClassificationInProgress: true });
                  }}
                  url={signalrUrl}
                  accessTokenFactory={this.getSignalrAccessToken}
                />
              ) : (
                <></>
              )}
              <LeftSideBar
                fileDataType={fileDataType}
                isUploadDisabled={isUploadDisabled}
                loading={loading}
                files={files}
                isDownloadDisabled={isDownloadDisabled}
                isDeleteDisabled={isDeleteDisabled}
                deleteAllFiles={this.deleteAllFiles}
              />
              <FileDataWrapper>
                <ErrorsWrapper>
                  {localStorageErrors
                    ? localStorageErrors.map((errorObjs) => {
                        return fileIds.includes(Number(errorObjs.fileId)) ? (
                          <ErrorPanel
                            errors={{
                              [errorObjs.fileName]: errorObjs.errors,
                            }}
                          />
                        ) : null;
                      })
                    : null}
                  <ServerErrorPanel error={serverError} />
                </ErrorsWrapper>
                {!isUploadDisabled && (
                  <UploadFileTile
                    onResetUpload={this.onResetUpload}
                    fileType={fileDataType}
                    isPrimaryButton={selectedFiles.length === 0}
                    onFail={this.showErrors}
                    onSuccess={this.onFileUploaded}
                    onStartUpload={this.startUploadProgress}
                    fileUploadInProgress={fileUploadInProgress}
                  />
                )}
                <FilesActions
                  files={files}
                  fileDataType={fileDataType}
                  goToClassification={this.goToClassification}
                  history={history}
                  isClassificationInProgress={isClassificationInProgress}
                  permissions={permissions}
                  startAutoClassification={this.startAutoClassification}
                />
                <FileRowsWrapper>
                  {selectedFiles.length === 0 ? <FileRowEmpty /> : null}
                  {selectedFiles
                    .sort((f) =>
                      f.fileState === IFileStatus.INVALID ||
                      f.fileState === IFileStatus.ERROR
                        ? -1
                        : 1
                    )
                    .map((file) => (
                      <FileRow
                        isDownloadDisabled={isDownloadDisabled}
                        isDeleteDisabled={isDeleteDisabled}
                        key={JSON.stringify(file)}
                        file={file}
                        onDeleteFile={this.deleteFile}
                      />
                    ))}
                </FileRowsWrapper>
              </FileDataWrapper>
            </>
          )}
        </MainContentWrapper>
        {isSuccessNotification && (
          <NotificationBar successMessage="Generating file... You will receive a notification when the file has been generated" />
        )}
        {isClassificationNotification && (
          <NotificationBar successMessage="Auto classification started... You will receive a notification when classification ends" />
        )}
      </React.Fragment>
    );
  }
}

export const Data = (props: RouteComponentProps) => (
  <ApiContext.Consumer>
    {({ permissions }) => <DataView {...props} permissions={permissions} />}
  </ApiContext.Consumer>
);
